import type {
    IteroPatient,
    IteroPatientPage,
    IteroPatientsFilterParams,
    IteroPatientsParams
} from '../../../../services/iteroBrowse';
import { mergeUniquePages, pageOffset } from './browsePaging.ts';
import {
    DEFAULT_LAST_SCAN_PRESET,
    lastScanDateFrom,
    type LastScanPreset
} from './filter-bar/chipPresets.ts';
import { DEFAULT_PATIENT_SORT, type PatientSort } from './patientSort.ts';
import { hasSearchText, patientSearchFields } from './searchInput.ts';

export const PATIENTS_PAGE_SIZE = 50;

export interface PatientFilterValues {
    search: string;
    lastScan: LastScanPreset;
    lastScanSince: string;
}

export const DEFAULT_PATIENT_FILTERS: PatientFilterValues = {
    search: '',
    lastScan: DEFAULT_LAST_SCAN_PRESET,
    lastScanSince: ''
};

export function buildPatientFilterParams(
    filters: PatientFilterValues,
    sort: PatientSort,
    today: string
): IteroPatientsFilterParams {
    const { firstName, lastName, chartNumber } = patientSearchFields(filters.search);
    return {
        firstName,
        lastName,
        chartNumber,
        lastScanDateFrom: lastScanDateFrom(filters.lastScan, filters.lastScanSince, today),
        sort: sort.by,
        order: sort.order,
        limit: PATIENTS_PAGE_SIZE
    };
}

export function patientPageParams(
    filterParams: IteroPatientsFilterParams,
    page: number
): IteroPatientsParams {
    return { ...filterParams, offset: pageOffset(page, filterParams.limit) };
}

// The chart filter is a "contains" match upstream, so the restore matches the patient by id after.
export function patientLookupParams(chartNumber: string): IteroPatientsParams {
    return {
        chartNumber: chartNumber.trim(),
        sort: DEFAULT_PATIENT_SORT.by,
        order: DEFAULT_PATIENT_SORT.order,
        offset: 0,
        limit: PATIENTS_PAGE_SIZE
    };
}

// An empty page ends the scroll even if the server offers another offset, so it can't loop.
export function nextPatientsOffset(page: IteroPatientPage): number | undefined {
    if (page.nextOffset === null || page.patients.length === 0) return undefined;
    return page.nextOffset;
}

export function mergePatientPages(pages: readonly IteroPatientPage[]): IteroPatient[] {
    return mergeUniquePages(
        pages,
        (page) => page.patients,
        (patient) => patient.id
    );
}

// Narrows the result set, so an empty table means "no match" rather than "no patients".
export function hasPatientSearchFilters(filters: PatientFilterValues, today: string): boolean {
    return (
        hasSearchText(filters.search) ||
        lastScanDateFrom(filters.lastScan, filters.lastScanSince, today) !== undefined
    );
}

export function isDefaultPatientFilters(filters: PatientFilterValues): boolean {
    return !hasSearchText(filters.search) && filters.lastScan === DEFAULT_PATIENT_FILTERS.lastScan;
}

function totalPages(total: number, pageSize: number): number {
    return Math.max(1, Math.ceil(total / pageSize));
}

// nextOffset is the server's word on whether another page exists; total only sizes the pager.
export function patientPageCount(
    page: number,
    pageSize: number,
    total: number,
    nextOffset: number | null
): number {
    if (nextOffset === null) return Math.max(1, page);
    return Math.max(totalPages(total, pageSize), page + 1);
}
